import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ChevronDown, BarChart3, Users, Shield, TrendingUp,
  Zap, ArrowRight, Target, Eye, MessageSquare, GitBranch,
  Bell, LineChart, Menu, X, FileDown
} from 'lucide-react';
import PDFDownloadLink from './PDFDownloadLink';

/* ───────── DATA ───────── */

const productItems = [
  {
    icon: TrendingUp,
    color: 'text-blue-400',
    bg: 'bg-blue-400/10',
    label: 'Risk Radar',
    desc: 'Pipeline health monitoring, spot stalled deals before they slip.',
    to: '/product/risk-radar',
  },
  {
    icon: BarChart3,
    color: 'text-indigo-400',
    bg: 'bg-indigo-400/10',
    label: 'Opportunities Pipeline',
    desc: 'Full deal tracking with risk scoring, stage analysis, and filters.',
    to: '/product/opportunities-pipeline',
  },
  {
    icon: Target,
    color: 'text-amber-400',
    bg: 'bg-amber-400/10',
    label: "Today's Focus",
    desc: 'Daily action prescriptions, one card per rep, zero guesswork.',
    to: '/product/todays-focus',
  },
  {
    icon: Users,
    color: 'text-emerald-400',
    bg: 'bg-emerald-400/10',
    label: 'Teams Overview',
    desc: 'Team performance, quota attainment, and rep activity at a glance.',
    to: '/product/teams-overview',
  },
  {
    icon: MessageSquare,
    color: 'text-violet-400',
    bg: 'bg-violet-400/10',
    label: 'AI Deal Chat',
    desc: 'Ask questions about any deal and get instant, data-backed answers.',
    to: '/product/ai-deal-chat',
  },
  {
    icon: Eye,
    color: 'text-cyan-400',
    bg: 'bg-cyan-400/10',
    label: 'Account Intelligence',
    desc: 'Deep account insights, contacts, history, risk factors, next steps.',
    to: '/product/account-intelligence',
  },
];

const coreFeatures = [
  { icon: Zap, label: 'Action Prescriptions', to: '/product/action-prescriptions' },
  { icon: LineChart, label: 'Pipeline Health Scoring', to: '/product/pipeline-health-scoring' },
  { icon: Shield, label: 'Deal Risk Signals', to: '/product/deal-risk-signals' },
  { icon: GitBranch, label: 'CRM Auto-Sync', to: '/product/crm-auto-sync' },
  { icon: Users, label: 'Role-Based Views', to: '/product/role-based-views' },
  { icon: Bell, label: 'Smart Notifications', to: '/product/smart-notifications' },
];

const solutionItems = [
  { label: 'CRO', to: '/solutions/cro' },
  { label: 'RevOps', to: '/solutions/revops' },
  { label: 'Sales Manager', to: '/solutions/sales-manager' },
  { label: 'Sales Reps', to: '/solutions/sales-reps' },
  { label: 'SDR / BDR', to: '/solutions/sdr-bdr' },
  { label: 'Customer Success Leader', to: '/solutions/cs-leader' },
  { label: 'Customer Success Manager', to: '/solutions/cs-manager' },
  { label: 'Enablement', to: '/solutions/enablement' },
  { label: 'Marketing', to: '/solutions/marketing' },
  { label: 'Product', to: '/solutions/product' },
];

const resourcesLeft = [
  { label: 'Documentation', to: '/resources/documentation' },
  { label: 'Build vs. Buy', to: '/buy-vs-build' },
  { label: 'Security', to: '/security' },
  { label: 'Playbook', to: '/resources/playbook' },
  { label: 'ROI Calculator', to: '/resources/roi-calculator' },
  { label: 'Partners', to: '/resources/partners' },
];

const resourcesRight = [
  { label: 'Origin Story', to: '/origin-story' },
  { label: 'Forsyt vs Gong', to: '/vs-gong' },
  { label: 'Forsyt vs Clari', to: '/vs-clari' },
  { label: 'Forsyt vs Scratchpad', to: '/vs-scratchpad' },
  { label: 'Blog', to: '/blog' },
];

/* ───────── MEGA DROPDOWN WRAPPER ───────── */

function MegaDropdown({ label, children, width = 'w-[600px]', testId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const timeout = useRef(null);

  const handleEnter = useCallback(() => {
    clearTimeout(timeout.current);
    setOpen(true);
  }, []);

  const handleLeave = useCallback(() => {
    timeout.current = setTimeout(() => setOpen(false), 150);
  }, []);

  useEffect(() => {
    return () => clearTimeout(timeout.current);
  }, []);

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <button
        onClick={() => setOpen((p) => !p)}
        className="flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors px-1 py-2"
        data-testid={testId}
      >
        {label}
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown Panel */}
      <div
        className={`absolute top-full left-1/2 -translate-x-1/2 pt-3 z-50 transition-all duration-200 ${
          open ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-2 pointer-events-none'
        }`}
      >
        <div
          className={`${width} bg-white border border-gray-200 rounded-xl shadow-2xl shadow-black/10 overflow-hidden`}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

/* ───────── PRODUCT DROPDOWN ───────── */

function ProductDropdown() {
  const navigate = useNavigate();
  return (
    <MegaDropdown label="Product" width="w-[680px]" testId="nav-product">
      <div className="flex">
        {/* Left, Products */}
        <div className="flex-1 p-5 border-r border-gray-100">
          <div className="space-y-1">
            {productItems.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className="flex items-start gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors group"
                data-testid={`nav-product-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className={`w-8 h-8 rounded-lg ${item.bg} flex items-center justify-center shrink-0 mt-0.5`}>
                  <item.icon className={`w-4 h-4 ${item.color}`} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                    {item.label}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{item.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Right, Core Features */}
        <div className="w-[240px] p-5">
          <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3 px-3">
            Core Features
          </p>
          <div className="space-y-0.5">
            {coreFeatures.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors group"
                data-testid={`nav-feature-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <item.icon className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
                <span className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors">
                  {item.label}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-gray-100 px-5 py-3 bg-gray-50">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors"
          data-testid="nav-product-try-sandbox"
        >
          Try the Sandbox
          <ArrowRight className="w-3 h-3" />
        </button>
      </div>
    </MegaDropdown>
  );
}

/* ───────── SOLUTIONS DROPDOWN ───────── */

function SolutionsDropdown() {
  return (
    <MegaDropdown label="Solutions" width="w-[280px]" testId="nav-solutions">
      <div className="p-4">
        <div className="space-y-0.5">
          {solutionItems.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              className="block px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors group"
              data-testid={`nav-solution-${item.label.toLowerCase().replace(/[\s\/]+/g, '-')}`}
            >
              <span className="text-sm text-gray-600 group-hover:text-gray-900 font-medium transition-colors">
                {item.label}
              </span>
            </Link>
          ))}
        </div>
      </div>
    </MegaDropdown>
  );
}

/* ───────── RESOURCES DROPDOWN ───────── */

function ResourcesDropdown() {
  return (
    <MegaDropdown label="Resources" width="w-[480px]" testId="nav-resources">
      <div className="flex">
        {/* Left column */}
        <div className="flex-1 p-5 border-r border-gray-100">
          <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3 px-3">
            Resources
          </p>
          <div className="space-y-0.5">
            <Link
              to="/plugins"
              data-testid="nav-resource-plugins"
              className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-blue-50 transition-colors group"
            >
              <span className="text-sm text-gray-900 group-hover:text-blue-700 font-semibold transition-colors">
                Micro SaaS Store
              </span>
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">
                New
              </span>
            </Link>
            <Link
              to="/community"
              data-testid="nav-resource-community"
              className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-blue-50 transition-colors group"
            >
              <span className="text-sm text-gray-900 group-hover:text-blue-700 font-semibold transition-colors">
                Revenue Leaders Community
              </span>
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                Invite only
              </span>
            </Link>
            {resourcesLeft.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className="block px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors group"
                data-testid={`nav-resource-${item.label.toLowerCase().replace(/[\s.]+/g, '-')}`}
              >
                <span className="text-sm text-gray-600 group-hover:text-gray-900 font-medium transition-colors">
                  {item.label}
                </span>
              </Link>
            ))}
          </div>
        </div>

        {/* Right column */}
        <div className="flex-1 p-5">
          <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3 px-3">
            Compare & Learn
          </p>
          <div className="space-y-0.5">
            {resourcesRight.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className="block px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors group"
                data-testid={`nav-learn-${item.label.toLowerCase().replace(/[\s.]+/g, '-')}`}
              >
                <span className="text-sm text-gray-600 group-hover:text-gray-900 font-medium transition-colors">
                  {item.label}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </MegaDropdown>
  );
}

/* ───────── MOBILE MENU ───────── */

function MobileMenu({ isOpen, onClose }) {
  const navigate = useNavigate();
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleSection = (section) => {
    setExpandedSection((prev) => (prev === section ? null : section));
  };

  const handleNavigate = (to) => {
    navigate(to);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 md:hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div className="absolute top-0 right-0 w-80 h-full bg-[#0b1221] border-l border-slate-700/50 overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-slate-700/40">
          <span className="text-white font-semibold text-lg">Menu</span>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="mobile-menu-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="p-4 space-y-1">
          {/* Product */}
          <div>
            <button
              onClick={() => toggleSection('product')}
              className="flex items-center justify-between w-full px-3 py-3 text-sm font-medium text-slate-200 hover:text-white rounded-lg hover:bg-white/5"
              data-testid="mobile-nav-product"
            >
              Product
              <ChevronDown className={`w-4 h-4 transition-transform ${expandedSection === 'product' ? 'rotate-180' : ''}`} />
            </button>
            {expandedSection === 'product' && (
              <div className="pl-4 space-y-1 mt-1">
                {productItems.map((item) => (
                  <button
                    key={item.label}
                    onClick={() => handleNavigate(item.to)}
                    className="block w-full text-left px-3 py-2 text-sm text-slate-400 hover:text-white rounded-lg hover:bg-white/5"
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Solutions */}
          <div>
            <button
              onClick={() => toggleSection('solutions')}
              className="flex items-center justify-between w-full px-3 py-3 text-sm font-medium text-slate-200 hover:text-white rounded-lg hover:bg-white/5"
              data-testid="mobile-nav-solutions"
            >
              Solutions
              <ChevronDown className={`w-4 h-4 transition-transform ${expandedSection === 'solutions' ? 'rotate-180' : ''}`} />
            </button>
            {expandedSection === 'solutions' && (
              <div className="pl-4 space-y-1 mt-1">
                {solutionItems.map((item) => (
                  <button
                    key={item.label}
                    onClick={() => handleNavigate(item.to)}
                    className="block w-full text-left px-3 py-2 text-sm text-slate-400 hover:text-white rounded-lg hover:bg-white/5"
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Direct links */}
          <button
            onClick={() => handleNavigate('/security')}
            className="block w-full text-left px-3 py-3 text-sm font-medium text-slate-200 hover:text-white rounded-lg hover:bg-white/5"
          >
            Security
          </button>
          <button
            onClick={() => handleNavigate('/resources/roi-calculator')}
            className="block w-full text-left px-3 py-3 text-sm font-medium text-slate-200 hover:text-white rounded-lg hover:bg-white/5"
          >
            ROI Calculator
          </button>

          {/* Resources */}
          <div>
            <button
              onClick={() => toggleSection('resources')}
              className="flex items-center justify-between w-full px-3 py-3 text-sm font-medium text-slate-200 hover:text-white rounded-lg hover:bg-white/5"
              data-testid="mobile-nav-resources"
            >
              Resources
              <ChevronDown className={`w-4 h-4 transition-transform ${expandedSection === 'resources' ? 'rotate-180' : ''}`} />
            </button>
            {expandedSection === 'resources' && (
              <div className="pl-4 space-y-1 mt-1">
                <button
                  onClick={() => handleNavigate('/plugins')}
                  data-testid="mobile-nav-plugins"
                  className="flex items-center justify-between w-full px-3 py-2 text-sm font-semibold text-white rounded-lg bg-blue-600/20 hover:bg-blue-600/30 border border-blue-400/30"
                >
                  <span>Micro SaaS Store</span>
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-blue-200">
                    New
                  </span>
                </button>
                <button
                  onClick={() => handleNavigate('/community')}
                  data-testid="mobile-nav-community"
                  className="flex items-center justify-between w-full px-3 py-2 text-sm font-semibold text-white rounded-lg bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-400/30"
                >
                  <span>Revenue Leaders Community</span>
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-200">
                    Invite only
                  </span>
                </button>
                {[...resourcesLeft, ...resourcesRight].map((item) => (
                  <button
                    key={item.label}
                    onClick={() => handleNavigate(item.to)}
                    className="block w-full text-left px-3 py-2 text-sm text-slate-400 hover:text-white rounded-lg hover:bg-white/5"
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-slate-700/40 pt-4 mt-4" />
        </nav>
      </div>
    </div>
  );
}

/* ───────── MAIN HEADER ───────── */

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="bg-white border-b border-gray-100" data-testid="site-header">
      {/* Announcement Strip - Plugin Store Launch */}
      <div className="relative overflow-hidden bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700">
        <div className="absolute inset-0 opacity-30 pointer-events-none" aria-hidden="true">
          <div className="absolute -top-12 -left-12 w-72 h-72 rounded-full bg-blue-400 blur-3xl" />
          <div className="absolute -bottom-12 -right-12 w-72 h-72 rounded-full bg-purple-400 blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-3.5">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-5 text-center">
            <p className="text-base sm:text-lg font-bold text-white tracking-tight flex items-center justify-center gap-2">
              <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider bg-white/20 border border-white/30 rounded-full px-2 py-0.5">New</span>
              Update CRM deals from WhatsApp, Botim, Slack or Teams. One bot, everywhere.
            </p>
            <Link
              to="/#crawl"
              onClick={(e) => {
                if (window.location.pathname === '/') {
                  const el = document.getElementById('crawl');
                  if (el) { e.preventDefault(); el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
                }
              }}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold bg-white text-slate-900 rounded-full hover:bg-blue-50 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all shadow-md whitespace-nowrap"
              data-testid="announcement-strip-link"
            >
              See how it works
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </div>

      {/* Main Nav */}
      <div className="max-w-7xl mx-auto px-6 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="shrink-0" aria-label="Forsyt Home" data-testid="nav-logo">
            <img
              src="/assets/gt687tca.png"
              alt="Forsyt"
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1" aria-label="Main navigation">
            <ProductDropdown />
            <SolutionsDropdown />

            <Link
              to="/security"
              className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors px-3 py-2"
              data-testid="nav-security"
            >
              Security
            </Link>
            <Link
              to="/resources/roi-calculator"
              className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors px-3 py-2"
              data-testid="nav-roi"
            >
              ROI Calculator
            </Link>

            <ResourcesDropdown />
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden text-gray-600 hover:text-blue-600"
              data-testid="mobile-menu-open"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>

      <MobileMenu isOpen={mobileOpen} onClose={() => setMobileOpen(false)} />
    </header>
  );
};

export default Header;
